import '@patsnap-ui/icon/assets/solid/CoreProductsChemical.svg'
import { structureColorConfig, structureLangConfig } from '@patsnap/synapse_common_config'
import { VUE_APP_PRODUCT_CHEMICAL_URL } from '@pharmsnap/pharmsnap-web/config'
import { useLocale } from '@pharmsnap/shared/src/composition'
import { EMPTY_PLACEHOLDER } from '@pharmsnap/shared/src/constants'
import { sharedCtx } from '@pharmsnap/shared/src/context'
import { IStructureItem } from '@pharmsnap/shared/src/types/drug'
import { openNewTab } from '@pharmsnap/shared/src/utils'
import { computed, defineComponent, PropType } from '@vue/composition-api'
import { Icon as PtIcon } from 'patsnap-biz'
import { BSourceInfo } from '../..'
import { BCopy } from '../BCopy/BCopy'

export const BStructureCard = defineComponent({
  name: 'BStructureCard',
  props: {
    data: {
      required: true,
      type: Object as PropType<IStructureItem>,
    },
    layout: {
      type: String as PropType<'vertical' | 'horizontal'>,
      default: 'vertical',
    },
    isXDC: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { tsText, localeUpcase, locale } = useLocale()

    function clickStructureFind() {
      sharedCtx.service.tracking.trackGeneral({
        event_code: 'VIEW_CHEMICAL_STRUCTURE',
        trigger_point: 'SIMILAR',
      })
      openNewTab(`${VUE_APP_PRODUCT_CHEMICAL_URL}/search#/structure?inchiKey=${props.data.inchi_key}`)
    }
    const imgUrl = computed(() => {
      return props.data.adc_figure?.file_path || props.data.base64
    })
    const inchiKey = computed(() => {
      if (props.data.inchi_key === '0') {
        return ''
      }
      return props.data.inchi_key
    })
    return { tsText, localeUpcase, clickStructureFind, locale, imgUrl, inchiKey }
  },
  methods: {
    renderFinderSimilar() {
      return (
        <span onClick={this.clickStructureFind} class="inline-flex items-center text-blue-default cursor-pointer hover:underline">
          <PtIcon style="font-size: 24px;" icon="SolidCoreProductsChemical"></PtIcon>
          <span class="text-sm leading-6 ml-1">{this.localeUpcase === 'CN' ? '查看相似结构' : 'Find Similar'}</span>
        </span>
      )
    },
    renderStructureType() {
      const tagType = this.data.structure_type

      if (!tagType) return null

      const textColor = structureColorConfig[tagType].color
      const bgColor = structureColorConfig[tagType].bgColor
      const tagName = structureLangConfig[tagType].shortName[this.locale]

      return (
        <span
          style={{ color: textColor, backgroundColor: bgColor }}
          class={['h-6 inline-flex items-center px-2 rounded text-xs leading-4 max-w-full truncate']}
        >
          {tagName}
        </span>
      )
    },
    renderInfo() {
      return (
        <div class="flex flex-col gap-6">
          {this.inchiKey && <div>{this.renderFinderSimilar()}</div>}
          {(this.data.formula || this.data.cas_no) && (
            <div class="flex justify-between gap-2">
              {!!this.data.formula && (
                <div>
                  <div class="text-text-t2 text-sm leading-5">{this.$tc('synapse_i18n.drug.structure.molecularFormula')}</div>
                  <div class="text-text-t1 text-sm leading-5 mt-1">{this.data.formula || EMPTY_PLACEHOLDER}</div>
                </div>
              )}
              {!!this.data.cas_no && (
                <div>
                  <div class="text-text-t2 text-sm leading-5">{this.$tc('synapse_i18n.drug.structure.casRegistry')}</div>
                  <div class="inline-flex items-center leading-5 mt-1" style="font-size: 0">
                    <span class="text-text-t1 text-sm">{this.data.cas_no || EMPTY_PLACEHOLDER}</span>
                    {this.data.cas_no ? <BCopy class="ml-2" copyVal={this.data.cas_no || ''} size={20}></BCopy> : null}
                  </div>
                </div>
              )}
            </div>
          )}
          {this.inchiKey && (
            <div>
              <div class="text-text-t2 text-sm leading-5">{this.$tc('synapse_i18n.drug.structure.standardInChIKey')}</div>
              <div class="text-text-t1 text-sm leading-5 mt-1">{this.inchiKey || EMPTY_PLACEHOLDER}</div>
            </div>
          )}
          {this.data.structure_type && (
            <div>
              <div class="text-text-t2 text-sm leading-5">{this.locale === 'cn' ? '结构类型' : 'Structure Type'}</div>
              <div class="mt-1">
                <span class="inline-flex items-center">{this.renderStructureType()}</span>
              </div>
            </div>
          )}
          {this.data.source_info?.length && (
            <div>
              <div class="text-text-t2 text-sm leading-5">{this.locale === 'cn' ? '来源' : 'Source'}</div>
              <div class="mt-1">
                <BSourceInfo sourceInfo={this.data.source_info} triggerPoint="STRUCTURE"></BSourceInfo>
              </div>
            </div>
          )}
        </div>
      )
    },
  },
  render() {
    if (this.isXDC) {
      return (
        <div class="border rounded border-gray-40 overflow-hidden p-6  flex">
          <div class="flex-1 text-center">
            <img
              alt="adc"
              class="cursor-pointer"
              src={this.imgUrl}
              onClick={() => {
                this.$emit('adcImageClicked', 0)
              }}
            ></img>
          </div>
          <div class="flex-shrink-0">{this.renderInfo()}</div>
        </div>
      )
    }
    if (this.layout === 'horizontal') {
      return (
        <div class="flex">
          <div class="flex-shrink-0 border border-gray-60 rounded">
            <img width={300} height={300} src={this.imgUrl} onClick={() => this.$emit('casImageClicked')} class="cursor-pointer"></img>
          </div>
          <div class="flex-1 overflow-hidden ml-4">{this.renderInfo()}</div>
        </div>
      )
    }

    return (
      <div class="w-[300px]">
        <div class="relative w-[300px] h-[300px] border border-gray-60 rounded flex flex-col justify-between overflow-hidden">
          {this.inchiKey && <div class="text-sm h-[30px] px-2 text-right flex items-center justify-end">{this.renderFinderSimilar()}</div>}
          <img
            class="cursor-pointer flex-1 w-[300px] h-[270px]"
            style="object-fit: contain;"
            src={this.imgUrl}
            onClick={() => this.$emit('casImageClicked')}
          />
        </div>
        <div>
          {this.data.formula && (
            <div style="word-break: break-word;" class="flex items-center mt-2 text-sm">
              <span>{this.data.formula}</span>
            </div>
          )}
          {this.inchiKey && (
            <div style="word-break: break-word;" class="flex items-center mt-2 text-sm">
              <span>{this.inchiKey}</span>
            </div>
          )}
          {this.data.cas_no && (
            <div class="flex items-center mt-2 text-sm">
              <span>{this.data.cas_no || EMPTY_PLACEHOLDER}</span>
              <BCopy copyVal={this.data.cas_no} class="ml-2"></BCopy>
            </div>
          )}
          {this.data.structure_type ? <div class="flex items-center mt-2">{this.renderStructureType()}</div> : null}
          {this.data.source_info?.length && (
            <div class="mt-2 flex items-center gap-2">
              <BSourceInfo sourceInfo={this.data.source_info} triggerPoint="STRUCTURE"></BSourceInfo>
            </div>
          )}
        </div>
      </div>
    )
  },
})
